import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';
import CopyButton from './CopyButton';
import './AnalyticsRangePanel.css';

/* =============================================================
   AnalyticsRangePanel — comprehensive, time-ranged analytics.

   scope="admin" -> GET /api/admin/analytics/range  (admin only)
   scope="user"  -> GET /api/user/analytics/range   (caller-scoped)

   Renders ONLY backend data: missing values show "—", empty ranges
   show "No activity" — nothing fabricated client-side. Auto-refreshes
   every 25s (live). Supports presets + a custom date range.
   ============================================================= */

const RANGES = [
  ['24h', '24H'],
  ['7d', '7D'],
  ['30d', '30D'],
  ['90d', '90D'],
  ['1y', '1Y'],
  ['all', 'All'],
  ['custom', 'Custom'],
];

const ADMIN_TILES = [
  { key: 'net_revenue_usd', label: 'Net Revenue', fmt: 'usd', series: 'revenue', delta: true, accent: '#39ff88' },
  { key: 'smm_revenue_usd', label: 'SMM Revenue (recorded)', fmt: 'usd', series: 'revenue', delta: true, accent: '#00e0ff' },
  { key: 'refunds_usd', label: 'Refunds', fmt: 'usd', accent: '#f87171' },
  { key: 'provider_cost_usd', label: 'Provider Cost', fmt: 'usd', series: 'provider_cost', accent: '#fb923c' },
  { key: 'gross_margin_usd', label: 'Gross Margin', fmt: 'usd', series: 'margin', sub: 'margin_pct', accent: '#b06bff' },
  { key: 'store_revenue_usd', label: 'Store Revenue', fmt: 'usd', delta: true, accent: '#fbbf24' },
  { key: 'orders', label: 'Orders', fmt: 'int', series: 'orders', delta: true, accent: '#00e0ff', breakdown: 'orders' },
  { key: 'avg_order_value_usd', label: 'Avg Order Value', fmt: 'usd', accent: '#7ad7ff' },
  { key: 'fulfillment_rate_pct', label: 'Fulfillment Rate', fmt: 'pct', accent: '#34d399' },
  { key: 'refund_rate_pct', label: 'Refund Rate', fmt: 'pct', accent: '#f87171' },
  { key: 'active_users', label: 'Active Users', fmt: 'int', series: 'active_users', accent: '#ff00ff', breakdown: 'active_users' },
  { key: 'new_users', label: 'New Users', fmt: 'int', accent: '#39ff88', breakdown: 'new_users' },
];
const USER_TILES = [
  { key: 'spend_usd', label: 'Spend', fmt: 'usd', series: 'spend', delta: true, accent: '#00e0ff' },
  { key: 'orders', label: 'Orders', fmt: 'int', series: 'orders', delta: true, accent: '#b06bff', breakdown: 'orders' },
  { key: 'store_spend_usd', label: 'Store Spend', fmt: 'usd', accent: '#fbbf24' },
  { key: 'streaming_revenue_usd', label: 'Streaming Revenue', fmt: 'usd', accent: '#39ff88' },
  { key: 'streams', label: 'Streams', fmt: 'int', accent: '#ff00ff' },
];

const ADMIN_CHARTS = [
  { label: 'Revenue', field: 'revenue', color: '#39ff88' },
  { label: 'Provider Cost', field: 'provider_cost', color: '#fb923c' },
  { label: 'Orders', field: 'orders', color: '#00e0ff' },
  { label: 'Active Users', field: 'active_users', color: '#ff00ff' },
];
const USER_CHARTS = [
  { label: 'Spend', field: 'spend', color: '#00e0ff' },
  { label: 'Orders', field: 'orders', color: '#b06bff' },
];

const STATUS_COLORS = {
  Completed: '#34d399', 'In progress': '#fbbf24', Processing: '#fbbf24',
  Partial: '#fb923c', Pending: '#00e0ff', Canceled: '#f87171', Refunded: '#a78bfa',
};

const fmtVal = (v, fmt) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (fmt === 'pct') return `${n}%`;
  if (fmt === 'usd') {
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 10_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return n.toLocaleString();
};

const dateStr = (ms) => new Date(ms).toISOString().slice(0, 10);
const shortWallet = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '—');
const WalletLink = ({ addr }) => (addr ? (
  <a href={`https://bscscan.com/address/${addr}`} target="_blank" rel="noopener noreferrer" title={addr}>{shortWallet(addr)}</a>
) : <span>—</span>);

// Format a bucket key (e.g. "2026-03-15" or "2026-03-15T14:00" or "2026-03")
// to a short axis label appropriate for its granularity.
const fmtBucketTick = (key) => {
  if (!key) return '';
  const s = String(key);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s.slice(11, 16);   // hour: "HH:MM"
  if (/^\d{4}-\d{2}-\d{2}$/.test(s))             return s.slice(5);         // day:  "MM-DD"
  if (/^\d{4}-\d{2}$/.test(s))                   return s;                   // month: "YYYY-MM"
  return s;
};

const Bars = ({ points, color = '#00e0ff', height = 64 }) => {
  const max = Math.max(1, ...points.map((p) => p.value));
  const n = points.length || 1;
  // Bar slot width — wider when there are fewer buckets so a 30-day or
  // 24-hour chart still feels like a real bar chart and not a flat line.
  const slot = n <= 24 ? 14 : n <= 32 ? 10 : n <= 100 ? 6 : 4;
  const barW = Math.max(1, slot - 2);
  const totalW = Math.max(n * slot, 60);

  // Axis ticks: start, middle, end (skip middle if only 1-2 points).
  const tickIdx = n <= 2 ? [0, n - 1] : [0, Math.floor(n / 2), n - 1];
  const seenTickIdx = new Set(tickIdx);

  return (
    <div className="arp-bars-wrap">
      <svg className="arp-bars" style={{ height }} viewBox={`0 0 ${totalW} 60`} preserveAspectRatio="none">
        {/* Per-bucket footprint marker so empty days still show a faint
            tick — makes daily progression visible at a glance. */}
        {points.map((p, i) => (
          <rect
            key={`f-${p.t || i}`}
            x={i * slot + (slot - barW) / 2}
            y={59}
            width={barW}
            height={1}
            fill={color}
            opacity={0.12}
          />
        ))}
        {/* Value bars */}
        {points.map((p, i) => {
          const h = Math.max((p.value / max) * 56, p.value > 0 ? 2 : 0);
          if (h <= 0) return null;
          return (
            <rect
              key={p.t || i}
              x={i * slot + (slot - barW) / 2}
              y={60 - h}
              width={barW}
              height={h}
              rx="1"
              fill={color}
              opacity={0.9}
            >
              <title>{`${p.t}: ${p.value}`}</title>
            </rect>
          );
        })}
      </svg>
      {n > 1 && (
        <div className="arp-bars-axis">
          {points.map((p, i) => (
            <span
              key={p.t || i}
              className={`arp-bars-tick ${seenTickIdx.has(i) ? 'arp-bars-tick--show' : ''}`}
            >
              {seenTickIdx.has(i) ? fmtBucketTick(p.t) : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const AnalyticsRangePanel = ({ scope = 'admin' }) => {
  const tiles = scope === 'admin' ? ADMIN_TILES : USER_TILES;
  const charts = scope === 'admin' ? ADMIN_CHARTS : USER_CHARTS;
  const endpoint = scope === 'admin' ? '/admin/analytics/range' : '/user/analytics/range';

  const [range, setRange] = useState('7d');
  const [customStart, setCustomStart] = useState(dateStr(Date.now() - 7 * 864e5));
  const [customEnd, setCustomEnd] = useState(dateStr(Date.now()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(tiles[0].key);
  const [drillService, setDrillService] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [entityData, setEntityData] = useState(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const inflight = useRef(false);

  const load = useCallback(async (silent = false) => {
    if (inflight.current) return;
    inflight.current = true;
    if (!silent) setLoading(true);
    try {
      const params = range === 'custom'
        ? { range: 'custom', start: customStart, end: customEnd }
        : { range };
      const res = await api.get(endpoint, { params });
      setData(res.data);
      setError('');
    } catch (e) {
      if (!silent) setData(null);
      setError(e?.response?.data?.message || e?.message || 'Failed to load analytics');
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, [endpoint, range, customStart, customEnd]);

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), 25000); // live refresh
    return () => clearInterval(id);
  }, [load]);

  const openService = useCallback(async (service) => {
    setDrillService(service);
    setDrillData(null);
    setDrillLoading(true);
    try {
      const params = range === 'custom'
        ? { service, range: 'custom', start: customStart, end: customEnd }
        : { service, range };
      const res = await api.get(endpoint.replace('/range', '/service'), { params });
      setDrillData(res.data);
    } catch (e) {
      setDrillData({ error: e?.response?.data?.message || e?.message || 'Failed to load detail' });
    } finally {
      setDrillLoading(false);
    }
  }, [endpoint, range, customStart, customEnd]);

  const sel = tiles.find((t) => t.key === selected) || tiles[0];
  const series = useMemo(() => data?.series || [], [data]);

  const seriesPoints = useCallback((field) => series.map((row) => {
    let value;
    if (field === 'margin') value = Number(row.revenue || 0) - Number(row.provider_cost || 0);
    else value = Number(row[field] || 0);
    return { t: row.t, value };
  }), [series]);

  const points = useMemo(() => (sel.series ? seriesPoints(sel.series) : []), [sel, seriesPoints]);
  const hasSeries = points.some((p) => p.value !== 0);

  const topServices = data?.top_services || [];
  const byProvider = data?.by_provider || [];
  const topSpenders = data?.top_spenders || [];
  const PROVIDER_LABELS = { smmflare: 'Provider A', morethanpanel: 'Provider B', smmfollows: 'Provider C' };
  const statusEntries = Object.entries(data?.status_breakdown || {});

  // When the selected tile drills into entities (active users / new users /
  // orders), fetch the underlying rows for the current range.
  const selBreakdown = sel.breakdown;
  useEffect(() => {
    if (!selBreakdown) { setEntityData(null); return undefined; }
    let cancelled = false;
    setEntityLoading(true);
    const params = range === 'custom'
      ? { metric: selBreakdown, range: 'custom', start: customStart, end: customEnd }
      : { metric: selBreakdown, range };
    api.get(endpoint.replace('/range', '/breakdown'), { params })
      .then((res) => { if (!cancelled) setEntityData(res.data); })
      .catch((e) => { if (!cancelled) setEntityData({ error: e?.response?.data?.message || e?.message || 'Failed to load' }); })
      .finally(() => { if (!cancelled) setEntityLoading(false); });
    return () => { cancelled = true; };
  }, [selBreakdown, range, customStart, customEnd, endpoint]);

  return (
    <div className="arp">
      <div className="arp-head">
        <h3 className="arp-title">{scope === 'admin' ? 'Platform Analytics' : 'Your Analytics'}</h3>
        <div className="arp-ranges" role="tablist" aria-label="Time range">
          {RANGES.map(([val, label]) => (
            <button
              key={val}
              type="button"
              className={`arp-range ${range === val ? 'active' : ''}`}
              onClick={() => setRange(val)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {range === 'custom' && (
        <div className="arp-custom">
          <label>From <input type="date" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} /></label>
          <label>To <input type="date" value={customEnd} min={customStart} max={dateStr(Date.now())} onChange={(e) => setCustomEnd(e.target.value)} /></label>
        </div>
      )}

      {error && !data ? (
        <div className="arp-error">{error}</div>
      ) : (
        <>
          {scope === 'admin' && data?.onchain_escrow && (
            <div className="arp-escrow">
              <div className="arp-escrow-head">
                <span>⛓ Live Escrow Contract <em>· on-chain truth</em></span>
                <a className="arp-escrow-addr" href={`https://bscscan.com/address/${data.onchain_escrow.contract}`} target="_blank" rel="noopener noreferrer">
                  {data.onchain_escrow.contract.slice(0, 6)}…{data.onchain_escrow.contract.slice(-4)} ↗
                </a>
              </div>
              <div className="arp-escrow-grid">
                <div className="arp-escrow-cell"><em>Collected (revenue)</em><b>${data.onchain_escrow.collected_usdt}</b></div>
                <div className="arp-escrow-cell"><em>Reserved</em><b>${data.onchain_escrow.reserved_usdt}</b></div>
                <div className="arp-escrow-cell"><em>User Available</em><b>${data.onchain_escrow.available_usdt}</b></div>
                <div className="arp-escrow-cell hl"><em>Total USDT in contract</em><b>${data.onchain_escrow.total_usdt_in_contract}</b></div>
              </div>
              <div className="arp-escrow-note">
                <b>Collected</b> is captured USDT actually in the contract. Recorded SMM revenue below comes from order
                records — a large gap means orders were billed without a successful on-chain capture.
              </div>
            </div>
          )}

          <div className="arp-tiles">
            {tiles.map((t) => {
              const kpi = data?.kpis?.[t.key];
              const value = kpi ? kpi.value : null;
              const delta = t.delta && kpi ? kpi.delta_pct : null;
              const sub = t.sub && kpi ? kpi[t.sub] : null;
              return (
                <button
                  key={t.key}
                  type="button"
                  className={`arp-tile ${selected === t.key ? 'active' : ''}`}
                  style={{ '--accent': t.accent }}
                  onClick={() => setSelected(t.key)}
                  aria-pressed={selected === t.key}
                >
                  <span className="arp-tile-label">{t.label}</span>
                  <span className="arp-tile-value">{loading && !data ? '…' : fmtVal(value, t.fmt)}</span>
                  <span className="arp-tile-foot">
                    {sub !== null && sub !== undefined && <em className="arp-tile-sub">{sub}% margin</em>}
                    {delta !== null && delta !== undefined && (
                      <em className={`arp-delta ${delta >= 0 ? 'up' : 'down'}`}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%</em>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Always-on trends grid */}
          <div className="arp-section-label">Trends · {range === 'custom' ? `${customStart} → ${customEnd}` : range.toUpperCase()}</div>
          <div className="arp-charts">
            {charts.map((c) => {
              const pts = seriesPoints(c.field);
              const total = pts.reduce((s, p) => s + p.value, 0);
              return (
                <div className="arp-chart" key={c.field} style={{ '--accent': c.color }}>
                  <div className="arp-chart-head">
                    <span>{c.label}</span>
                    <b>{c.field.includes('revenue') || c.field.includes('cost') || c.field === 'spend' ? fmtVal(total, 'usd') : Math.round(total).toLocaleString()}</b>
                  </div>
                  {pts.some((p) => p.value !== 0) ? <Bars points={pts} color={c.color} height={56} /> : <div className="arp-empty sm">No activity</div>}
                </div>
              );
            })}
          </div>

          {/* Admin: per-provider profitability + VIP spenders */}
          {scope === 'admin' && byProvider.length > 0 && (
            <>
              <div className="arp-section-label">Profit by provider</div>
              <div className="arp-table-wrap">
                <table className="arp-table">
                  <thead>
                    <tr><th>Provider</th><th>Revenue</th><th>Cost</th><th>Margin</th><th>Margin %</th><th>Orders</th></tr>
                  </thead>
                  <tbody>
                    {byProvider.map((p) => (
                      <tr key={p.provider}>
                        <td className="arp-svc">{PROVIDER_LABELS[p.provider] || p.provider}</td>
                        <td>{fmtVal(p.revenue, 'usd')}</td>
                        <td>{fmtVal(p.provider_cost, 'usd')}</td>
                        <td>{fmtVal(p.margin, 'usd')}</td>
                        <td style={{ color: p.margin_pct >= 40 ? '#34d399' : p.margin_pct >= 20 ? '#fbbf24' : '#f87171' }}>{p.margin_pct}%</td>
                        <td>{p.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {scope === 'admin' && topSpenders.length > 0 && (
            <>
              <div className="arp-section-label">Top spenders</div>
              <div className="arp-table-wrap">
                <table className="arp-table">
                  <thead><tr><th>Wallet</th><th>Spent</th><th>Orders</th></tr></thead>
                  <tbody>
                    {topSpenders.map((u) => (
                      <tr key={u.wallet}>
                        <td className="arp-svc">
                          <a href={`https://bscscan.com/address/${u.wallet}`} target="_blank" rel="noopener noreferrer" title={u.wallet}>
                            {u.wallet ? `${u.wallet.slice(0, 6)}…${u.wallet.slice(-4)}` : '—'}
                          </a>
                        </td>
                        <td>{fmtVal(u.spend_usd, 'usd')}</td>
                        <td>{u.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Drill-down for the selected tile */}
          <div className="arp-detail" style={{ '--accent': sel.accent }}>
            <div className="arp-detail-head">
              <span className="arp-detail-title">{sel.label} · detail</span>
              <span className="arp-detail-value">{fmtVal(data?.kpis?.[sel.key]?.value, sel.fmt)}</span>
            </div>
            {sel.series ? (
              hasSeries ? <Bars points={points} color={sel.accent} height={90} /> : <div className="arp-empty">No activity in this range.</div>
            ) : (
              <div className="arp-note">A point-in-time metric — see the breakdown below.</div>
            )}

            {sel.key === 'orders' && statusEntries.length > 0 && (
              <div className="arp-breakdown">
                {statusEntries.map(([st, count]) => (
                  <span key={st} className="arp-chip" style={{ '--c': STATUS_COLORS[st] || '#9aa' }}>{st} <b>{count}</b></span>
                ))}
              </div>
            )}

            {(sel.key.includes('revenue') || sel.key.includes('cost') || sel.key.includes('margin') || sel.key === 'spend_usd') && topServices.length > 0 && (
              <div className="arp-table-wrap">
                <table className="arp-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      {scope === 'admin' ? <th>Revenue</th> : <th>Spend</th>}
                      {scope === 'admin' && <th>Cost</th>}
                      {scope === 'admin' && <th>Margin</th>}
                      <th>Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topServices.map((s, i) => (
                      <tr key={i} className="arp-row-click" onClick={() => openService(s.service)} title="Click for detail">
                        <td className="arp-svc">{s.service}</td>
                        <td>{fmtVal(scope === 'admin' ? s.revenue : s.spend, 'usd')}</td>
                        {scope === 'admin' && <td>{fmtVal(s.provider_cost, 'usd')}</td>}
                        {scope === 'admin' && <td>{fmtVal(s.margin, 'usd')}</td>}
                        <td>{s.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {sel.breakdown && (
              <>
                <div className="arp-section-label">
                  {sel.breakdown === 'active_users' ? 'Active wallets' : sel.breakdown === 'new_users' ? 'New wallets' : 'Orders'}
                  {entityData?.rows ? ` (${entityData.rows.length})` : ''}
                </div>
                {entityLoading ? (
                  <div className="arp-empty">Loading…</div>
                ) : entityData?.error ? (
                  <div className="arp-error">{entityData.error}</div>
                ) : entityData?.rows?.length ? (
                  <div className="arp-table-wrap">
                    <table className="arp-table">
                      {sel.breakdown === 'active_users' && (
                        <thead><tr><th>Wallet</th><th>Orders</th><th>Spend</th><th>Last order</th></tr></thead>
                      )}
                      {sel.breakdown === 'new_users' && (
                        <thead><tr><th>Wallet</th><th>Joined</th><th>Orders</th><th>Spend</th></tr></thead>
                      )}
                      {sel.breakdown === 'orders' && (
                        <thead><tr>{scope === 'admin' && <th>Wallet</th>}<th>Service</th><th>Qty</th><th>Status</th><th>Charged</th><th>When</th></tr></thead>
                      )}
                      <tbody>
                        {entityData.rows.map((row, i) => {
                          if (sel.breakdown === 'active_users') {
                            return (
                              <tr key={i}>
                                <td className="arp-svc"><WalletLink addr={row.wallet} /></td>
                                <td>{row.orders}</td>
                                <td>{fmtVal(row.spend_usd, 'usd')}</td>
                                <td>{row.last_order_at ? new Date(row.last_order_at).toLocaleString() : '—'}</td>
                              </tr>
                            );
                          }
                          if (sel.breakdown === 'new_users') {
                            return (
                              <tr key={i}>
                                <td className="arp-svc"><WalletLink addr={row.wallet} /></td>
                                <td>{row.member_since ? new Date(row.member_since).toLocaleDateString() : '—'}</td>
                                <td>{row.orders}</td>
                                <td>{fmtVal(row.spend_usd, 'usd')}</td>
                              </tr>
                            );
                          }
                          return (
                            <tr key={row.db_id || i}>
                              {scope === 'admin' && <td className="arp-svc"><WalletLink addr={row.wallet} /></td>}
                              <td className="arp-svc">{row.service}</td>
                              <td>{Number(row.quantity || 0).toLocaleString()}</td>
                              <td><span className="arp-chip sm" style={{ '--c': STATUS_COLORS[row.status] || '#9aa' }}>{row.status}</span></td>
                              <td>{fmtVal(row.charged_usd, 'usd')}</td>
                              <td>{new Date(row.created_at).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="arp-empty">No entries in this range.</div>
                )}
              </>
            )}

            {data?.window && (
              <div className="arp-window">
                {new Date(data.window.start).toLocaleString()} → {new Date(data.window.end).toLocaleString()}
                {loading && <span className="arp-loading"> · refreshing…</span>}
              </div>
            )}
          </div>
        </>
      )}

      {drillService && (
        <div className="arp-modal-overlay" onClick={() => setDrillService(null)}>
          <div className="arp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="arp-modal-close" type="button" onClick={() => setDrillService(null)}>×</button>
            <h4 className="arp-modal-title">{drillService}</h4>
            {drillLoading ? (
              <div className="arp-empty">Loading…</div>
            ) : drillData?.error ? (
              <div className="arp-error">{drillData.error}</div>
            ) : drillData ? (
              <>
                <div className="arp-modal-stats">
                  <div><em>Revenue</em><b>{fmtVal(drillData.totals.revenue, 'usd')}</b></div>
                  {scope === 'admin' && <div><em>Provider Cost</em><b>{fmtVal(drillData.totals.provider_cost, 'usd')}</b></div>}
                  {scope === 'admin' && <div><em>Margin</em><b>{fmtVal(drillData.totals.margin, 'usd')}</b></div>}
                  <div><em>Refunds</em><b>{fmtVal(drillData.totals.refunds, 'usd')}</b></div>
                  <div><em>Orders</em><b>{drillData.totals.orders}</b></div>
                  <div><em>Quantity</em><b>{Number(drillData.totals.quantity || 0).toLocaleString()}</b></div>
                </div>

                {Object.keys(drillData.status_breakdown || {}).length > 0 && (
                  <div className="arp-breakdown">
                    {Object.entries(drillData.status_breakdown).map(([st, count]) => (
                      <span key={st} className="arp-chip" style={{ '--c': STATUS_COLORS[st] || '#9aa' }}>{st} <b>{count}</b></span>
                    ))}
                  </div>
                )}

                <div className="arp-section-label">Recent orders ({drillData.orders.length})</div>
                {drillData.orders.length === 0 ? (
                  <div className="arp-empty">No orders for this service in range.</div>
                ) : (
                  <div className="arp-table-wrap">
                    <table className="arp-table">
                      <thead>
                        <tr>
                          {scope === 'admin' && <th>Wallet</th>}
                          <th>Qty</th>
                          <th>Status</th>
                          <th>Charged</th>
                          <th>When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drillData.orders.map((o) => (
                          <tr key={o.db_id}>
                            {scope === 'admin' && (
                              <td className="arp-svc">
                                {o.wallet ? (
                                  <span className="arp-wallet-cell">
                                    <a
                                      href={`https://bscscan.com/address/${o.wallet}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={o.wallet}
                                    >
                                      {`${o.wallet.slice(0, 6)}…${o.wallet.slice(-4)}`}
                                    </a>
                                    <CopyButton text={o.wallet} label="Copy wallet address" />
                                  </span>
                                ) : '—'}
                              </td>
                            )}
                            <td>{Number(o.quantity || 0).toLocaleString()}</td>
                            <td><span className="arp-chip sm" style={{ '--c': STATUS_COLORS[o.status] || '#9aa' }}>{o.status}</span></td>
                            <td>{fmtVal(o.charged_usd, 'usd')}</td>
                            <td>{new Date(o.created_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsRangePanel;
